import { useForm, usePage } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { SharedProps } from '@/types';

interface EditProjectFormProps {
  eventSlug: string;
  projectId: string;
  currentOnelinerShort: string | null;
}

export function EditProjectForm({
  eventSlug,
  projectId,
  currentOnelinerShort,
}: EditProjectFormProps) {
  const { flash } = usePage<SharedProps>().props;
  const { data, setData, patch, processing, errors } = useForm({
    onelinerShort: currentOnelinerShort || '',
  });

  const characterCount = data.onelinerShort.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Edit Project</CardTitle>
        <CardDescription>
          Update project details for voting and social sharing
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            patch(`/admin/${eventSlug}/projects/${projectId}`, {
              preserveScroll: true,
            });
          }}
          className="space-y-6"
        >
          <div className="space-y-2">
            <Label htmlFor="onelinerShort">Short Oneliner (Spanish)</Label>
            <Input
              id="onelinerShort"
              name="onelinerShort"
              placeholder="e.g., Mejora tu oratoria con análisis IA"
              disabled={processing}
              maxLength={60}
              value={data.onelinerShort}
              onChange={(e) => setData('onelinerShort', e.target.value)}
            />
            <p className="text-muted-foreground text-sm">
              A concise description in Spanish (max 60 characters) for OpenGraph
              images and voting pages.{' '}
              <span
                className={
                  characterCount > 60 ? 'text-red-600' : 'text-muted-foreground'
                }
              >
                {characterCount}/60 characters
              </span>
            </p>
            {errors.onelinerShort && (
              <p className="font-medium text-destructive text-sm">
                {errors.onelinerShort}
              </p>
            )}
          </div>

          {flash.notice && (
            <div className="rounded-md border border-green-200 bg-green-50 p-4">
              <p className="text-green-600 text-sm">{flash.notice}</p>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button type="submit" disabled={processing}>
              {processing ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
